// disabling eslint temporarily for legacy files until they can be refactored
/* eslint-disable */
import React, { useEffect, useState } from "react";
import { Chart } from "chart.js";
import { Spinner } from "../../../../components/Spinner";
import { Row, Col } from "react-bootstrap";
import language from "../../../utils/en.json";

// Formats the data passed into the GenericTrendsChart to be in the correct form for charts.js
export const formatTrendData = (data, xAxisKey, yAxisKey) => {
  function compare(a, b) {
    if (a.x < b.x) {
      return -1;
    }
    if (a.x > b.x) {
      return 1;
    }
    return 0;
  }

  let formattedData = [];
  for (let i = 0; i < data?.length; i++) {
    formattedData.push({
      x:
        data[i][xAxisKey].toString().length === 10
          ? new Date(0).setUTCSeconds(data[i][xAxisKey])
          : data[i][xAxisKey], // Date must be in milliseconds
      y: data[i][yAxisKey],
    });
  }

  return formattedData.sort(compare);
};

// Calculates the left-most bound of the chart by searching all data objects
export const getLeftBoundOfChart = (data) => {
  let minXValue;
  for (const dataSet of data) {
    minXValue = minXValue <= dataSet.data[0] ? minXValue : dataSet.data[0];
  }
  return minXValue;
};

// Calculates the maximum x-value in the data objects to set the right-most bound of the chart
export const getMaxDataPoint = (data) => {
  let maxXValue;
  for (const dataSet of data) {
    maxXValue =
      maxXValue >= dataSet.data[dataSet.data.length - 1]
        ? maxXValue
        : dataSet.data[dataSet.data.length - 1];
  }
  return maxXValue;
};

/**
 * A generic trend chart component. Currently supports multiple datasets for a line chart with x-axis representing time (months) and an integer y-axis.
 *
 * @param {Boolean} isLoading (Required) Whether or not the parent component is loading. Translated to isParentLoading as the component has a local isLoading state as well
 * @param {Boolean} isError (Required) Whether or not the parent component encountered an error (generally a failed data call). Determines when to display the "No data" view of the chart
 * @param {Object} chartRef  (Required) The reference object to the chart component. This is needed to identify one chart from another on a page
 * @param {Array} data (Required) The dataset to be passed into the chart configuration. All chart styling is also found within this object
 * @param {Object} dateRange (Required) The date range for the chart. This is received via the dateRangePicker in the subheader
 * @param {String} chartId (Optional) The html id of the chart
 * @param {Boolean} showLegend (Optional) Whether or not to display a legend below the chart
 * @param {String} legendPosition (Optional) Position of legend respective to the chart ('top', 'bottom', 'left', 'right')
 * @param {String} alignLegend (Optional) How to align the legend in its position ('start', 'center', 'end)
 * @param {String} xAxisLabel (Optional) The x-axis label
 * @param {String} yAxisLabel (Optional) The y-axis label
 * @param {String} chartHeight (Optional) The height of the chart
 */
export const GenericTrendsChart = (props) => {
  const {
    isLoading: isParentLoading,
    isError,
    chartId,
    canvasRef,
    data,
    dateRange,
    showLegend,
    legendPosition,
    alignLegend,
    xAxisLabel, // eslint-disable-line no-unused-vars
    yAxisLabel,
    chartHeight,
  } = props;

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    if (
      data?.length &&
      dateRange?.startDate &&
      dateRange?.endDate &&
      canvasRef?.current
    ) {
      initChart();
    }
    setIsLoading(false);
  }, [data, dateRange, canvasRef]); // eslint-disable-line react-hooks/exhaustive-deps

  const initChart = () => {
    Chart.defaults.global.defaultFontFamily = "'Sofia Pro', sans-serif";

    const minXValue = getLeftBoundOfChart(data);
    const maxXValue = getMaxDataPoint(data);

    const config = {
      type: "line",
      data: {
        labels: [dateRange.startDate, dateRange.endDate],
        datasets: data,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: showLegend,
          position: legendPosition,
          align: alignLegend,
          labels: {
            boxWidth: 8,
            padding: 20,
            fontFamily: "'Sofia Pro', 'Helvetica', 'sans-serif'",
            usePointStyle: true,
          },
        },
        title: {
          display: false,
        },
        scales: {
          xAxes: [
            {
              type: "time",
              time: {
                unit: "month",
              },
              display: true,
              distribution: "linear",
              scaleLabel: {
                display: false,
              },
              gridLines: true,
              ticks: {
                source: "auto",
                display: true,
                min: minXValue,
                max: maxXValue,
                fontSize: 13,
                padding: 10,
                autoSkip: true,
              },
            },
          ],
          yAxes: [
            {
              display: true,
              scaleLabel: {
                display:
                  yAxisLabel === undefined || yAxisLabel === "" ? false : true,
                labelString: yAxisLabel,
              },
              gridLines: {
                display: true,
                drawBorder: false,
                offsetGridLines: false,
                drawTicks: true,
                borderDash: [3, 4],
                zeroLineWidth: 1,
                zeroLineBorderDash: [3, 4],
              },
              ticks: {
                display: true,
                min: 0,
                beginAtZero: true,
                fontSize: 13,
                stepSize: 10,
                padding: 10,
              },
            },
          ],
        },
        hover: {
          mode: "point",
          intersect: false,
        },
        tooltips: {
          enabled: true,
          intersect: false,
          mode: "nearest",
          bodySpacing: 5,
          yPadding: 10,
          xPadding: 10,
          caretPadding: 0,
          displayColors: false,
          backgroundColor: "#333333",
          titleFontColor: "#ffffff",
          cornerRadius: 4,
          footerSpacing: 0,
          titleSpacing: 0,
        },
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
          },
        },
        plugins: {
          datalabels: false,
        },
      },
    };

    let ctx = canvasRef.current.getContext("2d");
    if (ctx) {
      var myChart = new Chart(ctx, { config: config });
      myChart.destroy();
      window.bar = new Chart(ctx, config);
    } else {
      window.bar = new Chart(canvasRef.current, config);
    }
  };

  return (
    <Row>
      <Col style={{ display: "flex", flexDirection: "column" }}>
        <div id={chartId}>
          {/* Loading view */}
          {(isLoading || isParentLoading) && (
            <div style={{ marginBottom: "-200px" }}>
              <Spinner />
            </div>
          )}

          {/* Error view */}
          {(isError || data === undefined) && (
            <div style={{ height: "100%" }} className="no-data-label">
              {language["GENERAL.NO_DATA"]}
            </div>
          )}

          {/* Trends chart view */}
          {data && data.length && (
            <div
              style={{
                display: isError || isLoading ? "none" : "",
                width: "100%",
                height: chartHeight,
              }}
            >
              <canvas
                className={"pr-3"}
                ref={canvasRef}
                height={250}
                id={chartId}
              />
            </div>
          )}
        </div>
      </Col>
    </Row>
  );
};
